import { Column, Container, Row } from '@cdab/scania/qpr/components/atoms'

import {
  useCssVariableBreakpoint,
  useGetStatisticsReport
} from '@cdab/scania/qpr/hooks'
import type { DOSVersion } from '@cdab/scania/qpr/schema'
import { ANALYTICS_EVENTS, useAnalytics } from '@cdab/scania/qpr/utils'
import { capitalizeFirstLetter } from '@cdab/utils'
import {
  TdsButton,
  TdsDatetime,
  TdsDropdown,
  TdsDropdownOption,
  TdsMessage
} from '@scania/tegel-react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FullWidth,
  MessagesRow,
  StyledFirstRow,
  StyledMessage
} from '../area-market-report/area-market-report.styles'

interface StatisticsReportProps {
  dosVersions: DOSVersion[]
}

export function StatisticsReport({ dosVersions }: StatisticsReportProps) {
  const { t } = useTranslation(['reports', 'common'])
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')
  const { trackEvent } = useAnalytics()

  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [selectedDosVersion, setSelectedDosVersion] = useState<number>(6)

  const [getStatisticsReportState, clickGetStatisticsReport] =
    useGetStatisticsReport(startDate, endDate, selectedDosVersion)

  const isReportDisabled =
    getStatisticsReportState.status === 'loading' ||
    !startDate ||
    !endDate ||
    selectedDosVersion === 0

  const onChangeDosVersion = useCallback((value: string | null) => {
    if (!value) return
    setSelectedDosVersion(parseInt(value))
  }, [])

  return (
    <Container
      fluid='normal'
      paddingOnColumns={!isLg}
      paddingOnContainer={!isLg}
    >
      <StyledFirstRow>
        <Column width={12} lg={8} offset={{ lg: 2 }}>
          <FullWidth>
            <Row>
              <Column width={12} lg={6} padding={isLg}>
                <TdsDatetime
                  modeVariant='secondary'
                  name='startDate'
                  noMinWidth={true}
                  label={t('date-from', { ns: 'common' })}
                  onTdsInput={e => setStartDate(e.target.value)}
                  type='date'
                />
              </Column>
              <Column width={12} lg={6} padding={isLg}>
                <TdsDatetime
                  modeVariant='secondary'
                  name='endDate'
                  noMinWidth={true}
                  label={t('date-to', { ns: 'common' })}
                  onTdsInput={e => setEndDate(e.target.value)}
                  type='date'
                />
              </Column>
            </Row>

            <Row className='tds-u-mt2'>
              <Column width={12} lg={6} padding={isLg}>
                <TdsDropdown
                  size='md'
                  modeVariant='secondary'
                  label='DOS Version'
                  defaultValue={'6'}
                  placeholder='Select DOS Version'
                  onTdsChange={e => onChangeDosVersion(e.detail.value)}
                >
                  {dosVersions.map(dosVersion => (
                    <TdsDropdownOption
                      key={dosVersion.version}
                      value={dosVersion.version.toString()}
                    >
                      {dosVersion.name}
                    </TdsDropdownOption>
                  ))}
                </TdsDropdown>
              </Column>
            </Row>

            <MessagesRow>
              {getStatisticsReportState.status === 'success' && (
                <StyledMessage className='tds-u-mt2'>
                  <TdsMessage
                    modeVariant='secondary'
                    header={`${capitalizeFirstLetter(t('statistics'))}: ${
                      getStatisticsReportState.message
                    } `}
                    variant={'success'}
                  />
                </StyledMessage>
              )}
              {getStatisticsReportState.status === 'error' && (
                <StyledMessage className='tds-u-mt2'>
                  <TdsMessage
                    modeVariant='secondary'
                    header={`${capitalizeFirstLetter(t('statistics'))}: ${
                      getStatisticsReportState.message
                    } `}
                    variant={'error'}
                  />
                </StyledMessage>
              )}
            </MessagesRow>

            <Row className='tds-u-mt2'>
              <TdsButton
                modeVariant='primary'
                size='md'
                text={capitalizeFirstLetter(t('statistics'))}
                onClick={() => {
                  if (isReportDisabled) return
                  trackEvent(ANALYTICS_EVENTS.StatisticsReportGenerate)
                  clickGetStatisticsReport()
                }}
                disabled={isReportDisabled}
              />
            </Row>
          </FullWidth>
        </Column>
      </StyledFirstRow>
    </Container>
  )
}
